/**
 * Driver Map — Admin Portal
 *
 * Shows live driver positions on a Google Map using @vis.gl/react-google-maps.
 * Pull-based refresh — admin clicks Refresh or enables auto-refresh.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { APIProvider, Map, AdvancedMarker, Pin, InfoWindow } from '@vis.gl/react-google-maps';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Slider,
  Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import { MyLocation, Refresh, Send, NotificationsActive } from '@mui/icons-material';
import { LIST_DRIVER_POSITIONS, SEND_DRIVER_NOTIFICATION } from '../graphql/queries';

const client = generateClient();
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

const REGIONS = [
  { label: 'Monrovia (City Centre)', lat: 6.2907, lng: -10.7969 },
  { label: 'Paynesville',            lat: 6.2895, lng: -10.7594 },
  { label: 'Sinkor',                 lat: 6.3100, lng: -10.7850 },
  { label: 'Red Light Market',       lat: 6.3200, lng: -10.7500 },
  { label: 'Custom',                 lat: 0,      lng: 0        },
];

const STATUS_COLOR: Record<string, string> = {
  ONLINE:  '#22c55e',
  OFFLINE: '#6b7280',
  BUSY:    '#f59e0b',
};

interface DriverPosition {
  driverId: string;
  latitude: number;
  longitude: number;
  status: string;
  vehicleType?: string;
  rating?: string;
  sampleTime?: string;
  distanceKm?: number;
}

export default function DriverMap() {
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [positions,    setPositions]    = useState<DriverPosition[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [lastRefresh,  setLastRefresh]  = useState<Date | null>(null);
  const [autoRefresh,  setAutoRefresh]  = useState(false);
  const [center,       setCenter]       = useState({ lat: REGIONS[0].lat, lng: REGIONS[0].lng });

  const [regionIdx,  setRegionIdx]  = useState(0);
  const [customLat,  setCustomLat]  = useState('');
  const [customLng,  setCustomLng]  = useState('');
  const [radiusKm,   setRadiusKm]   = useState(10);

  const [selectedDriver, setSelectedDriver] = useState<DriverPosition | null>(null);
  const [notifyDriver,   setNotifyDriver]   = useState<DriverPosition | null>(null);
  const [notifyType,     setNotifyType]     = useState<'subscription' | 'push'>('subscription');
  const [notifyMsg,      setNotifyMsg]      = useState('');
  const [sending,        setSending]        = useState(false);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      const region = REGIONS[regionIdx];
      const isCustom = regionIdx === REGIONS.length - 1;
      const centerLat = isCustom ? parseFloat(customLat) : region.lat;
      const centerLng = isCustom ? parseFloat(customLng) : region.lng;

      const result: any = await client.graphql({
        query: LIST_DRIVER_POSITIONS,
        variables: {
          centerLat: isNaN(centerLat) ? null : centerLat,
          centerLng: isNaN(centerLng) ? null : centerLng,
          radiusKm,
        },
      });

      const items: DriverPosition[] = result.data?.listDriverPositions?.items ?? [];
      setPositions(items);
      setLastRefresh(new Date());
      if (!isNaN(centerLat) && !isNaN(centerLng)) {
        setCenter({ lat: centerLat, lng: centerLng });
      }
    } catch (err) {
      console.error('Failed to fetch driver positions:', err);
    } finally {
      setLoading(false);
    }
  }, [regionIdx, customLat, customLng, radiusKm]);

  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh) autoRefreshRef.current = setInterval(fetchPositions, 30_000);
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, fetchPositions]);

  const handleSendNotification = async () => {
    if (!notifyDriver || !notifyMsg.trim()) return;
    setSending(true);
    try {
      const result: any = await client.graphql({
        query: SEND_DRIVER_NOTIFICATION,
        variables: {
          input: {
            driverId: notifyDriver.driverId,
            message: notifyMsg.trim(),
            notificationType: notifyType,
            title: 'Message from LibRide Admin',
          },
        },
      });
      if (result.data?.sendDriverNotification?.success) {
        setNotifyDriver(null);
        setNotifyMsg('');
      } else {
        alert(`Failed: ${result.data?.sendDriverNotification?.message ?? 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to send notification:', err);
    } finally {
      setSending(false);
    }
  };

  const isCustom = regionIdx === REGIONS.length - 1;

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Region</InputLabel>
              <Select value={regionIdx} label="Region" onChange={e => setRegionIdx(Number(e.target.value))}>
                {REGIONS.map((r, i) => <MenuItem key={i} value={i}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>

            {isCustom && (
              <>
                <TextField size="small" label="Latitude"  value={customLat} onChange={e => setCustomLat(e.target.value)}  sx={{ width: 120 }} />
                <TextField size="small" label="Longitude" value={customLng} onChange={e => setCustomLng(e.target.value)} sx={{ width: 120 }} />
              </>
            )}

            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="textSecondary">Radius: {radiusKm} km</Typography>
              <Slider value={radiusKm} min={1} max={50} step={1} onChange={(_, v) => setRadiusKm(v as number)} size="small" />
            </Box>

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={fetchPositions}
              disabled={loading}
              sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
            >
              Refresh
            </Button>

            <Tooltip title="Auto-refresh every 30 seconds">
              <ToggleButton
                value="auto"
                selected={autoRefresh}
                onChange={() => setAutoRefresh(p => !p)}
                size="small"
                sx={{ borderColor: '#000', color: autoRefresh ? '#fff' : '#000', bgcolor: autoRefresh ? '#000' : 'transparent' }}
              >
                <MyLocation fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            <Chip label={`${positions.length} drivers`} size="small" />
            <Chip label={`${positions.filter(p => p.status === 'ONLINE').length} online`}  color="success" size="small" />
            <Chip label={`${positions.filter(p => p.status === 'BUSY').length} busy`}     color="warning" size="small" />
            {lastRefresh && (
              <Typography variant="caption" color="textSecondary" alignSelf="center">
                Last refresh: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Map */}
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          mapId="libride-driver-map"
          style={{ width: '100%', height: 560, borderRadius: 8 }}
          defaultCenter={center}
          center={center}
          defaultZoom={12}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl
        >
          {positions.map(driver => (
            <AdvancedMarker
              key={driver.driverId}
              position={{ lat: driver.latitude, lng: driver.longitude }}
              title={driver.driverId}
              onClick={() => setSelectedDriver(driver)}
            >
              <Pin
                background={STATUS_COLOR[driver.status] ?? '#6b7280'}
                borderColor="#fff"
                glyphColor="#fff"
                glyph="🚗"
              />
            </AdvancedMarker>
          ))}

          {selectedDriver && (
            <InfoWindow
              position={{ lat: selectedDriver.latitude, lng: selectedDriver.longitude }}
              onCloseClick={() => setSelectedDriver(null)}
            >
              <Box sx={{ minWidth: 180, p: 0.5 }}>
                <Typography variant="subtitle2">Driver</Typography>
                <Typography variant="caption" color="textSecondary" display="block">{selectedDriver.driverId}</Typography>
                <Typography variant="body2">Status: <b>{selectedDriver.status}</b></Typography>
                <Typography variant="body2">Type: {selectedDriver.vehicleType ?? '—'}</Typography>
                <Typography variant="body2">Rating: {selectedDriver.rating ?? '—'}</Typography>
                {selectedDriver.distanceKm != null && (
                  <Typography variant="body2">Distance: {selectedDriver.distanceKm} km</Typography>
                )}
                <Button
                  size="small"
                  variant="contained"
                  sx={{ mt: 1, bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
                  onClick={() => { setNotifyDriver(selectedDriver); setSelectedDriver(null); }}
                >
                  Send Notification
                </Button>
              </Box>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>

      {/* Notification dialog */}
      <Dialog open={!!notifyDriver} onClose={() => setNotifyDriver(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notification to Driver</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Driver: <b>{notifyDriver?.driverId}</b> · Status: <b>{notifyDriver?.status}</b>
          </Typography>
          <ToggleButtonGroup
            value={notifyType} exclusive
            onChange={(_, v) => v && setNotifyType(v)}
            size="small" sx={{ mb: 2 }}
          >
            <ToggleButton value="subscription"><Send fontSize="small" sx={{ mr: 0.5 }} />In-App</ToggleButton>
            <ToggleButton value="push"><NotificationsActive fontSize="small" sx={{ mr: 0.5 }} />Push</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            fullWidth multiline rows={3}
            label="Message"
            value={notifyMsg}
            onChange={e => setNotifyMsg(e.target.value)}
            placeholder="Enter message to send to driver..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDriver(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendNotification}
            disabled={sending || !notifyMsg.trim()}
            sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
